import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PhoneLink } from '../../ui-module/phone-link.js';
import { useTenantConfig } from '../../tenant-config-module/use-tenant-config.js';

const ICON_POP = {
  initial: { scale: 0.6, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { type: 'spring' as const, stiffness: 300, damping: 18 },
};

/**
 * „Seite nicht gefunden“ für alle Pfade außerhalb von `/cancel/<token>`
 * (z. B. `/admin`, `/`, Tippfehler).
 *
 * Bewusst **kein** State-Machine-Screen: der Absage-Flow ist gar nicht
 * gestartet, es gibt keinen Token und keine Termininfo. Optik lehnt sich an
 * den Technical-Error an (Headline, Icon, Text, Telefon-CTA), aber neutral
 * statt rot — es ist kein Fehler der Anwendung, sondern eine falsche Adresse.
 * `data-screen` erlaubt deterministisches Targeting in Tests.
 */
export function NotFoundScreen(): JSX.Element {
  const { t } = useTranslation();
  const config = useTenantConfig();
  const messageText = t('notFound.message', { phoneNumber: config.phoneNumber });

  return (
    <div className="flex flex-col items-center gap-6" data-screen="not-found">
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        {t('notFound.headline')}
      </h1>
      <motion.div
        className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-soft"
        initial={ICON_POP.initial}
        animate={ICON_POP.animate}
        transition={ICON_POP.transition}
        aria-label={t('notFound.iconAlt')}
        role="img"
      >
        <SearchX className="h-8 w-8 text-brand" strokeWidth={3} />
      </motion.div>
      <p role="status" className="max-w-xs text-center text-sm leading-relaxed text-foreground">
        {messageText}
      </p>
      <PhoneLink phoneNumber={config.phoneNumber} label={t('technicalError.callLabel')} />
    </div>
  );
}
